import { currentLocale, tr } from '../services/i18n.js';

export function isChallengeRevealed(challenge, now = Date.now()) {
  const t = new Date(challenge?.reveal_at || 0).getTime();
  return Number.isFinite(t) && t > 0 && t <= now;
}

export function challengeRevealTime(challenge) {
  const t = new Date(challenge?.reveal_at || 0).getTime();
  return Number.isFinite(t) && t > 0 ? t : null;
}

export function formatQuizClosingDate(challenge, locale = currentLocale(), timeZone) {
  const t = challengeRevealTime(challenge);
  if (!t) return tr('Clôture non définie', 'Closing time not set');
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {})
  }).format(new Date(t));
}

export function formatQuizTimeRemaining(challenge, now = Date.now()) {
  const t = challengeRevealTime(challenge);
  if (!t) return tr('Temps restant indisponible', 'Time remaining unavailable');
  const diff = t - now;
  if (diff <= 0) return tr('Clôturé', 'Closed');

  const totalMinutes = Math.ceil(diff / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days) parts.push(`${days} ${tr('j', 'd')}`);
  if (hours || days) parts.push(`${hours} h`);
  if (!days) parts.push(`${minutes} min`);
  return tr(`Temps restant : ${parts.join(' ')}`, `Time remaining: ${parts.join(' ')}`);
}

export function defaultRevealAt(now = Date.now()) {
  const d = new Date(now);
  d.setHours(23, 59, 0, 0);
  if (d.getTime() <= now) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

export function quizFrames(challenge, max = 4) {
  const frames = Array.isArray(challenge?.frame_previews) ? challenge.frame_previews : [];
  return frames.filter((f) => typeof f === 'string' && f.startsWith('data:image/')).slice(0, max);
}

export function compactAiAnswer(ai = {}) {
  return [
    ai.title,
    ai.summary,
    ai.key_moment,
    ai.interpretation
  ].filter(Boolean).join('\n\n');
}

// Instant (en secondes) réellement capturé pour l'image `idx`. Aligné sur la
// logique de debrief.js (frameToSeconds) : on lit le timestamp exact persisté
// (raw.frame_times) ; à défaut (anciennes analyses), repli sur la formule
// d'extraction historique basée sur le segment.
export function quizFrameTimeAt(idx, raw = {}) {
  const i = Number.isFinite(idx) ? idx : 0;
  const times = Array.isArray(raw?.frame_times) ? raw.frame_times : null;
  if (times && Number.isFinite(times[i])) return times[i];
  const fc = Number(raw?.frame_count) > 0 ? Number(raw.frame_count) : 8;
  const seg = raw?.segment && Number.isFinite(raw.segment.start) ? raw.segment : null;
  const start = seg ? seg.start : 0;
  const end = seg && Number.isFinite(seg.end) ? seg.end : 15;
  const span = Math.max(0.1, end - start);
  const step = span / fc;
  return Math.min(end - 0.01, start + step * i + 0.1);
}

// Convertit un identifiant de phase ('full', 'phase-2', un index numérique…) en
// index de comparaison. Retourne NaN si non interprétable.
function parsePhaseIndex(phaseId) {
  if (Number.isInteger(phaseId)) return phaseId;
  const m = String(phaseId ?? '').match(/\d+/);
  return m ? Number(m[0]) : NaN;
}

// Options de phase proposées pour créer un quiz à partir d'un débrief.
// Toujours au moins "Analyse complète" ; chaque comparaison exploitable de
// raw.key_frame_comparisons devient une phase avec un label lisible.
export function listQuizPhaseOptions(raw = {}) {
  const options = [{ id: 'full', index: null, label: tr('Analyse complète', 'Full analysis'), detail: '' }];
  const comparisons = Array.isArray(raw?.key_frame_comparisons) ? raw.key_frame_comparisons : [];
  const times = Array.isArray(raw?.frame_times) ? raw.frame_times : null;

  comparisons.forEach((c, i) => {
    if (!c || typeof c !== 'object') return;
    const fromF = Number(c.from_frame);
    const toF = Number(c.to_frame);
    const hasFrames = Number.isFinite(fromF) && Number.isFinite(toF);
    const detail = String(c.description || c.coaching_point || c.tactical_breakdown || c.change_type || '').trim();
    // Phase inutile si on n'a ni bornes d'images ni texte associé.
    if (!hasFrames && !detail) return;

    let label;
    if (hasFrames && times && Number.isFinite(times[fromF]) && Number.isFinite(times[toF])) {
      label = `${tr('Phase', 'Phase')} ${i + 1} · ${times[fromF].toFixed(1)}s → ${times[toF].toFixed(1)}s`;
    } else if (hasFrames) {
      label = `${tr('Phase', 'Phase')} ${i + 1} · ${tr('Images', 'Frames')} ${fromF + 1} → ${toF + 1}`;
    } else {
      label = `${tr('Phase', 'Phase')} ${i + 1}`;
    }

    options.push({ id: `phase-${i}`, index: i, label, detail: detail.slice(0, 80) });
  });

  return options;
}

// Construit les champs d'insertion d'un match_challenge pour la phase choisie.
// 'full' (ou phase invalide) => comportement historique : analyse complète.
// Sinon : frames limitées à la phase, ai_answer recentré sur la comparaison
// choisie tout en conservant les champs utiles au scoring/compactAiAnswer.
export function buildPhaseChallengePayload(raw = {}, phaseId = 'full') {
  const safeRaw = raw && typeof raw === 'object' ? raw : {};
  const fullPreviews = Array.isArray(safeRaw.frame_previews) ? safeRaw.frame_previews : [];

  const fullPayload = () => ({
    title: safeRaw.title || tr('Quiz tactique', 'Tactical quiz'),
    prompt: tr('Analyse cette séquence sans voir la réponse IA.', 'Analyze this sequence before seeing the AI answer.'),
    frame_previews: fullPreviews,
    ai_answer: safeRaw
  });

  if (!phaseId || phaseId === 'full') return fullPayload();

  const index = parsePhaseIndex(phaseId);
  const comparisons = Array.isArray(safeRaw.key_frame_comparisons) ? safeRaw.key_frame_comparisons : [];
  const c = Number.isInteger(index) ? comparisons[index] : null;
  if (!c || typeof c !== 'object') return fullPayload();

  const fromF = Number(c.from_frame);
  const toF = Number(c.to_frame);
  const hasFrames = Number.isFinite(fromF) && Number.isFinite(toF);
  const fullTimes = Array.isArray(safeRaw.frame_times) ? safeRaw.frame_times : [];

  // Bornes [lo, hi] des images de la phase, clampées sur les previews dispo.
  const lo = hasFrames ? Math.max(0, Math.min(fromF, toF)) : 0;
  const hiRaw = hasFrames ? Math.max(fromF, toF) : fullPreviews.length - 1;
  const hi = Math.min(hiRaw, fullPreviews.length - 1);
  const canSlice = hasFrames && fullPreviews.length > 0 && lo <= hi;

  let phasePreviews;
  let phaseTimes;
  let comparison;
  if (canSlice) {
    phasePreviews = fullPreviews.slice(lo, hi + 1);
    phaseTimes = fullTimes.length ? fullTimes.slice(lo, hi + 1) : [];
    // Comparaison re-basée sur les indices de la phase, bornée à la fenêtre.
    const clamp = (n) => Math.max(0, Math.min(phasePreviews.length - 1, n - lo));
    comparison = { ...c, from_frame: clamp(fromF), to_frame: clamp(toF) };
  } else {
    // Bornes inexploitables : repli previews complètes (fallback raisonnable).
    phasePreviews = fullPreviews;
    phaseTimes = fullTimes;
    comparison = c;
  }
  if (!phasePreviews.length) phasePreviews = fullPreviews;

  // Recentre summary/key_moment sur la phase pour que compactAiAnswer (réponse
  // IA révélée) et le scoring reflètent bien la phase choisie.
  const phaseSummary = [c.description, c.tactical_breakdown, c.attacking_structure, c.defensive_structure, c.destabilization]
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter(Boolean)
    .join('\n\n') || safeRaw.summary || '';
  const phaseKeyMoment = String(c.coaching_point || '').trim() || safeRaw.key_moment || '';

  const fromTime = hasFrames ? quizFrameTimeAt(fromF, safeRaw) : null;
  const toTime = hasFrames ? quizFrameTimeAt(toF, safeRaw) : null;

  const aiAnswer = {
    title: safeRaw.title || tr('Quiz tactique', 'Tactical quiz'),
    summary: phaseSummary,
    key_moment: phaseKeyMoment,
    interpretation: safeRaw.interpretation,
    segment: safeRaw.segment,
    frame_count: phasePreviews.length,
    frame_times: phaseTimes,
    frame_previews: phasePreviews,
    key_frame_comparisons: [comparison],
    quiz_phase: {
      phase_index: index,
      from_frame: hasFrames ? fromF : null,
      to_frame: hasFrames ? toF : null,
      from_time: fromTime,
      to_time: toTime
    }
  };

  const phaseNum = index + 1;
  return {
    title: `${safeRaw.title || tr('Quiz tactique', 'Tactical quiz')} · ${tr('Phase', 'Phase')} ${phaseNum}`,
    prompt: tr(
      `Analyse la phase ${phaseNum} de cette séquence sans voir la réponse IA.`,
      `Analyze phase ${phaseNum} of this sequence before seeing the AI answer.`
    ),
    frame_previews: phasePreviews,
    ai_answer: aiAnswer
  };
}
